import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList, CalendarDays, Users, FileText, BarChart3, ScrollText, TrendingUp, Clock, MapPin, Mail, Send } from "lucide-react";

const CARDS = [
  { key: "leads", label: "Leads", icon: ClipboardList, path: "/leads", color: "text-blue-500", bg: "bg-blue-500/10" },
  { key: "calendar", label: "Calendar", icon: CalendarDays, path: "/calendar", color: "text-emerald-500", bg: "bg-emerald-500/10" },
  { key: "contracts", label: "Contract Templates", icon: FileText, path: "/contract-templates", color: "text-violet-500", bg: "bg-violet-500/10", roles: ["super_admin", "company_admin"] },
  { key: "employees", label: "Users", icon: Users, path: "/employees", color: "text-amber-500", bg: "bg-amber-500/10", roles: ["super_admin", "company_admin"] },
  { key: "venues", label: "Birthday Venues", icon: MapPin, path: "/venues", color: "text-pink-500", bg: "bg-pink-500/10", roles: ["super_admin", "company_admin"] },
  { key: "blast", label: "Mass Blast", icon: Send, path: "/blast", color: "text-indigo-500", bg: "bg-indigo-500/10", roles: ["super_admin", "company_admin"] },
  { key: "reports", label: "Reports", icon: BarChart3, path: "/reports", color: "text-purple-500", bg: "bg-purple-500/10" },
  { key: "audit", label: "Audit Log", icon: ScrollText, path: "/audit", color: "text-rose-500", bg: "bg-rose-500/10", roles: ["super_admin"] },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filteredCards = CARDS.filter(
    (c) => !c.roles || c.roles.includes(user?.role)
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Welcome back, {user?.first_name}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border-border">
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <Card className="border-border hover:-translate-y-0.5 transition-transform" data-testid="kpi-pending">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1">
                  <Clock size={14} />
                  Pending Leads
                </div>
                <p className="text-2xl font-mono font-bold">{stats?.pending_leads ?? 0}</p>
              </CardContent>
            </Card>
            <Card className="border-border hover:-translate-y-0.5 transition-transform" data-testid="kpi-upcoming">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1">
                  <CalendarDays size={14} />
                  Upcoming Events
                </div>
                <p className="text-2xl font-mono font-bold">{stats?.upcoming_events ?? 0}</p>
              </CardContent>
            </Card>
            <Card className="border-border hover:-translate-y-0.5 transition-transform" data-testid="kpi-total">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1">
                  <ClipboardList size={14} />
                  Total Leads
                </div>
                <p className="text-2xl font-mono font-bold">{stats?.total_leads ?? 0}</p>
              </CardContent>
            </Card>
            <Card className="border-border hover:-translate-y-0.5 transition-transform" data-testid="kpi-conversion">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider mb-1">
                  <TrendingUp size={14} />
                  Conversion
                </div>
                <p className="text-2xl font-mono font-bold">{stats?.conversion_rate ?? 0}%</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.key}
              data-testid={`card-${card.key}`}
              className="border-border cursor-pointer hover:-translate-y-0.5 transition-transform active:scale-[0.98]"
              onClick={() => navigate(card.path)}
            >
              <CardHeader className="pb-2">
                <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center`}>
                  <Icon size={20} className={card.color} />
                </div>
              </CardHeader>
              <CardContent>
                <CardTitle className="text-lg font-outfit">{card.label}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage {card.label.toLowerCase()}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
